/* @moldura padrao */
/**
 * page.tsx (funcionarios) — A lista das pessoas. Tema AdminUX.
 *
 * Funcionário é a PESSOA; usuário é o LOGIN. O cadastro é UM só: a ficha
 * (`form.tsx`, em página própria) tem as abas, e é lá que o login se cria e se
 * vincula. Esta tela é só a lista.
 *
 * SÓCIO APARECE AQUI. A lista é de pessoas, e tirar a Luciana dela porque ela
 * também é sócia seria perder uma vendedora de vista. Quem separa é o vínculo:
 * é ele que decide quem entra em ponto, férias e folha — não quem aparece na
 * tela. Ver a decisão P15 em `.claude/plans/ativo/pessoal-2026/`.
 *
 * Portado do projeto irmão `webapp-rm-ops` (09/08/2026).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { SITUACAO_BADGE, VINCULO_ROTULO } from './tipos';
import type { Funcionario } from './tipos';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

type Aba = 'ativos' | 'desligados' | 'todos';

export function FuncionariosPage() {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  // ABAS, e não um interruptor "mostrar desligados": o interruptor esconde em
  // que modo a lista está, e quem chega numa tela sem alguém não sabe se a
  // pessoa não existe ou se está fora do filtro. Três abas com uma sempre acesa
  // respondem isso de relance.
  const [aba, setAba] = useState<Aba>('ativos');
  const navigate = useNavigate();

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      // Busca todo mundo e separa aqui: são nove pessoas, e trocar de aba sem
      // ida ao servidor é o que permite mostrar a contagem de cada uma.
      setFuncionarios(
        await api.get<Funcionario[]>('/funcionarios?incluir_desligados=true'),
      );
    } catch (err) {
      setErro(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível carregar os funcionários.',
      );
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  const ativos = useMemo(
    () => funcionarios.filter((f) => f.situacao !== 'desligado'),
    [funcionarios],
  );
  const desligados = useMemo(
    () => funcionarios.filter((f) => f.situacao === 'desligado'),
    [funcionarios],
  );
  const visiveis =
    aba === 'ativos' ? ativos : aba === 'desligados' ? desligados : funcionarios;

  const ABAS: { k: Aba; nome: string; n: number }[] = [
    { k: 'ativos', nome: 'Ativos', n: ativos.length },
    { k: 'desligados', nome: 'Desligados', n: desligados.length },
    { k: 'todos', nome: 'Todos', n: funcionarios.length },
  ];

  return (
    <CustomMolduraPagina
      titulo="Funcionários"
      descricao="As pessoas da equipe. Quem tem login aparece também em Usuários."
    >

      <div className="card adminuiux-card">
        <div className="card-body">
          <ul className="nav nav-tabs mb-3">
            {ABAS.map((t) => (
              <li className="nav-item" key={t.k}>
                <button
                  type="button"
                  className={`nav-link ${aba === t.k ? 'active' : ''}`}
                  onClick={() => setAba(t.k)}
                >
                  {t.nome}{' '}
                  <span className="badge text-bg-secondary ms-1">{t.n}</span>
                </button>
              </li>
            ))}
          </ul>

          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : visiveis.length === 0 ? (
            <div className="py-5 text-center text-secondary">
              Ninguém nesta aba.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0">
                <thead>
                  <tr className="text-secondary small text-uppercase">
                    <th>Nome</th>
                    <th>Função</th>
                    <th>Loja</th>
                    <th>Acesso</th>
                    <th>Admissão</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map((f) => (
                    // Linha inteira clicável: abrir a ficha é a ação óbvia, e
                    // obrigar a mirar num botãozinho é ruim de usar.
                    <tr
                      key={f.id}
                      onClick={() => navigate(`/funcionarios/${f.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="fw-medium">
                        <div className="d-flex align-items-center gap-2">
                          {f.foto ? (
                            <img
                              src={f.foto}
                              alt={f.nome}
                              className="rounded-circle object-fit-cover"
                              style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                          ) : (
                            <span
                              className="rounded-circle bg-light d-flex align-items-center justify-content-center text-secondary small"
                              style={{ width: '1.75rem', height: '1.75rem' }}
                            >
                              {f.nome.charAt(0).toUpperCase()}
                            </span>
                          )}
                          {/* Nome curto em destaque, completo embaixo: é o
                              curto que se usa no dia a dia. */}
                          <div className="lh-sm">
                            <div>{f.apelido || f.nome}</div>
                            {f.apelido && f.apelido !== f.nome && (
                              <div className="text-secondary small">{f.nome}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          {f.funcao_nome ? (
                            <span className="badge text-bg-light border">
                              {f.funcao_nome}
                            </span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                          {/* Sócio ganha selo; funcionário não — o comum não
                              precisa de etiqueta. E os dois convivem: a
                              Luciana é sócia e vendedora. */}
                          {VINCULO_ROTULO[f.vinculo] && (
                            <span className="badge text-bg-info">
                              {VINCULO_ROTULO[f.vinculo]}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-secondary">{f.loja_nome ?? '—'}</td>
                      <td className="text-secondary">
                        {f.usuario ? (
                          <span>
                            {f.usuario}
                            {f.perfil_nome && (
                              <span className="d-block small">{f.perfil_nome}</span>
                            )}
                          </span>
                        ) : (
                          <span className="badge text-bg-light border">Sem login</span>
                        )}
                      </td>
                      <td className="text-secondary">
                        {f.admissao ? formatarData(f.admissao) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${SITUACAO_BADGE[f.situacao].classe}`}>
                          {SITUACAO_BADGE[f.situacao].rotulo}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CustomMolduraPagina>
  );
}
